import { useMemo, useState } from "react";
import { ResponsiveSankey } from "@nivo/sankey";
import { AlertTriangle, GitBranch, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { useSankey, type SankeyView } from "@/hooks/useStrategy";
import { formatMoney } from "@/lib/utils";

const VIEWS: { id: SankeyView; label: string; description: string }[] = [
  {
    id: "money",
    label: "Money flow",
    description: "Where every dollar goes — gross income → tax buckets → take-home → personal spend.",
  },
  {
    id: "tax",
    label: "Tax flow",
    description: "Just the tax bill, broken down by federal bracket, SE tax, NIIT, additional Medicare, and WI.",
  },
  {
    id: "whatif",
    label: "What-if S-Corp",
    description: "If you elected S-Corp today: salary vs distribution and the FICA savings vs Schedule C.",
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  income: "#34d399",
  hub: "#60a5fa",
  tax: "#f87171",
  "tax-bracket": "#fb7185",
  operating: "#a78bfa",
  save: "#22d3ee",
  takehome: "#86efac",
  spend: "#fbbf24",
  savings: "#facc15",
  info: "#94a3b8",
};

export function SankeyCard() {
  const [view, setView] = useState<SankeyView>("money");
  const sankey = useSankey(view);

  const data = useMemo(() => {
    if (!sankey.data || sankey.data.blocked) return null;
    return {
      nodes: sankey.data.nodes.map((n) => ({
        id: n.id,
        nodeColor: CATEGORY_COLORS[n.category] ?? "#94a3b8",
        category: n.category,
      })),
      links: sankey.data.links.map((l) => ({
        source: l.source,
        target: l.target,
        value: Math.round(l.value),
      })),
    };
  }, [sankey.data]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-primary" />
              Cash flow Sankey
            </CardTitle>
            <CardDescription>
              {VIEWS.find((v) => v.id === view)!.description}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-1">
            {VIEWS.map((v) => (
              <Button
                key={v.id}
                size="sm"
                variant={v.id === view ? "primary" : "outline"}
                onClick={() => setView(v.id)}
              >
                {v.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sankey.isLoading && (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading flow…
          </div>
        )}
        {!sankey.isLoading && sankey.data?.blocked && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{sankey.data.reason ?? "Tax engine has blocking gaps."}</span>
          </div>
        )}
        {!sankey.isLoading && data && data.links.length === 0 && (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            Not enough data yet to render this view.
          </div>
        )}
        {!sankey.isLoading && data && data.links.length > 0 && (
          <div className="h-[420px]">
            <ResponsiveSankey
              data={data}
              margin={{ top: 12, right: 160, bottom: 12, left: 160 }}
              align="justify"
              colors={(node) => (node as { nodeColor?: string }).nodeColor ?? "#94a3b8"}
              nodeOpacity={1}
              nodeHoverOthersOpacity={0.35}
              nodeThickness={14}
              nodeSpacing={18}
              nodeBorderWidth={0}
              nodeBorderRadius={3}
              linkOpacity={0.3}
              linkHoverOpacity={0.6}
              linkContract={3}
              enableLinkGradient
              labelPosition="outside"
              labelOrientation="horizontal"
              labelPadding={8}
              labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
              valueFormat={(v) => formatMoney(v)}
              theme={{
                background: "transparent",
                text: { fill: "#a1a1aa", fontSize: 11 },
                tooltip: {
                  container: {
                    background: "#1a1816",
                    color: "#e8e4df",
                    border: "1px solid #2e2a26",
                    borderRadius: 6,
                    padding: 8,
                  },
                },
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
